import Big from 'big.js';
import { FeeDetails } from '../config/feeConfig/feeConfig';
import { ChainType } from '../providers/GatekeeperNetworkService/GatekeeperNetworkServiceData';

export type WalletBalanceDetails = {
  usd: string;
  crypto: string;
  hasSufficientFunds: boolean;
};

export function cryptoAmountBigInt(amount: string, chain: ChainType): Big {
  const SOL_LAMPORTS_PER_SOL = new Big('1000000000');
  const ETH_WEI_PER_ETH = new Big('1000000000000000000');
  const CSPR_MOTES_PER_CSPR = new Big('1000000000');

  const balanceBig = new Big(amount);

  let result: Big;
  switch (chain) {
    case 'solana':
      result = balanceBig.div(SOL_LAMPORTS_PER_SOL);
      break;
    case 'ethereum':
      result = balanceBig.div(ETH_WEI_PER_ETH);
      break;
    case 'casper':
      result = balanceBig.div(CSPR_MOTES_PER_CSPR);
      break;
    default:
      throw new Error('Unsupported chain type');
  }

  return result; // Returns a string with full precision
}
/**
 * Returns whether the wallet has sufficient funds to cover the transaction fees
 * @param {string} walletCryptoBalance
 * @param {ChainType} chain
 * @param {string} feesTotal
 * @returns {boolean}
 */
export const hasSufficientFunds = (walletCryptoBalance: string, chain: ChainType, feesTotal: string): boolean => {
  // the total crypto fee is calculate like this: const totalCryptoFees = R.pipe(R.map(R.prop('amount')), R.sum)(cryptoTransactionFees).toFixed(6);
  // so we need to convert it to a number before comparing
  const balanceBig = new Big(walletCryptoBalance);
  const feesTotalBig = new Big(feesTotal);
  return balanceBig.minus(feesTotalBig).gte(0);
};

export const getWalletBalanceDetails = (
  walletCryptoBalance: string,
  chain: ChainType,
  fees: FeeDetails,
): WalletBalanceDetails => {
  if (!fees.crypto.total) {
    throw new Error('Crypto fees total is not defined');
  }
  const balanceBig = cryptoAmountBigInt(walletCryptoBalance, chain);
  const sufficientFunds = hasSufficientFunds(walletCryptoBalance, chain, fees.crypto.total);
  const usdBalance = fees.usdToCryptoRate ? balanceBig.times(fees.usdToCryptoRate).toFixed(2) : '';
  return {
    usd: usdBalance,
    crypto: walletCryptoBalance,
    hasSufficientFunds: sufficientFunds,
  };
};
